import { useMemo } from "react";

import type { IGetProductsEssentialsQuery, ProductNicotineStrength } from "@magento/types";

import { getConfig } from "@jti/fe-common";
import type { MagentoStockStatus } from "@jti/magento-types";
import { useQuery } from "@jti/rest-api";

import { createMagentoClient } from "../../clients/getClient";
import { getIsInStock, getStockStatus, normalizeProductNicotineStrengthData } from "../../utils";

export type UseProductsDynamicDataOptions = {
  productsSkus: string[];
  pageSize?: number;
  isTrial?: boolean;
};

export type DynamicProductData = {
  isInStock?: boolean;
  stockStatus?: MagentoStockStatus;
  currentPrice?: number;
  oldPrice?: number;
  discount?: number;
  nicotineStrength?: ProductNicotineStrength;
  productData: {
    name?: string;
    image?: string;
    description?: string;
    colorOptions?: ColorOption[];
    isBundle?: boolean;
  };
};

type ProductItemData = IGetProductsEssentialsQuery["products"]["items"][0];

type ColorOption = { value: string; label: string };

const getColorOptionsForConfigurableProduct = (productData: ProductItemData): ColorOption[] => {
  if (productData.__typename !== "ConfigurableProduct") {
    return [];
  }

  return (
    productData?.configurable_options?.[0]?.values
      ?.filter((option) => Boolean(option?.swatch_data?.value))
      .map((option) => ({
        value: option.swatch_data.value,
        label: option.label ?? "",
      })) ?? []
  );
};

export const useProductsDynamicData = ({ productsSkus, isTrial = false }: UseProductsDynamicDataOptions) => {
  const config = getConfig();
  const magentoClient = createMagentoClient({ isTrial, config });

  const {
    data: productResponseData,
    isFetching,
    isSuccess,
  } = useQuery<IGetProductsEssentialsQuery>({
    queryKey: ["PRODUCT_ESSENTIAL_QUERY", productsSkus, { isTrial }],
    enabled: productsSkus.length > 0,
    queryFn: () => {
      return magentoClient.getProductsEssentialsBySku(productsSkus, productsSkus.length);
    },
  });

  const currency = productResponseData?.currency?.base_currency_code;

  const products = useMemo(() => {
    const productsData = productResponseData?.products?.items ?? [];
    const productsPrices = productResponseData?.products_prices.items ?? [];

    return productsData.reduce<Record<string, DynamicProductData>>((acc, productData) => {
      const productType = productData?.__typename;

      const productPrice = productsPrices.find(({ sku }) => sku === productData.sku);

      const isConfigurableProduct = productType === "ConfigurableProduct";
      const isBundle = productType === "BundleProduct";

      const sku = isConfigurableProduct ? productData.variants[0]?.product?.sku || "" : productData.sku || "";

      const isInStock = getIsInStock(sku, productData);
      const stockStatus = getStockStatus(sku, productData);

      const minimumPrice = productPrice?.price_range?.minimum_price;

      const colorOptions = getColorOptionsForConfigurableProduct(productData);
      const nicotineStrength = normalizeProductNicotineStrengthData(productData.strength_details);

      acc[productData.sku] = {
        isInStock,
        stockStatus,
        currentPrice: minimumPrice?.final_price?.value,
        oldPrice: minimumPrice?.regular_price?.value,
        discount: minimumPrice?.discount?.percent_off,
        nicotineStrength,
        productData: {
          name: productData?.name,
          image: productData?.thumbnail.url,
          description: productData?.short_description.html,
          colorOptions,
          isBundle,
        },
      };
      return acc;
    }, {});
  }, [productResponseData]);

  return {
    isProductsDataLoading: isFetching,
    isProductsDataLoaded: !isFetching && isSuccess,
    products,
    currency,
  };
};
